"use client";

import { useEffect, useMemo, useRef } from "react";
import {
  BellRing,
  ChevronLeft,
  ChevronRight,
  Eye,
  MessageSquareText,
  ShieldAlert,
  TrendingUp,
  type LucideIcon,
} from "lucide-react";
import { ScrollReveal } from "@/components/ui/scroll-reveal";
import { websiteBodyText, websiteCardTitleText, websiteSectionTitleText } from "@/app/(website)/_components/website-type-scale";

type FocusCard = {
  id: string;
  title: string;
  blurb: string;
  accent: string;
  accentSoft: string;
  accentSecondary: string;
  icon: LucideIcon;
  visual: "clarity" | "warnings" | "briefing" | "answers" | "coverage";
};

const focusCards: FocusCard[] = [
  {
    id: "clarity",
    title: "See what matters first",
    blurb: "Signals rise above noise before they turn into expensive surprises.",
    accent: "#a855f7",
    accentSoft: "rgba(168,85,247,0.24)",
    accentSecondary: "#38bdf8",
    icon: Eye,
    visual: "clarity",
  },
  {
    id: "warnings",
    title: "Catch issues earlier",
    blurb: "Weak signals become visible while there is still time to act.",
    accent: "#ef4444",
    accentSoft: "rgba(239,68,68,0.24)",
    accentSecondary: "#f97316",
    icon: ShieldAlert,
    visual: "warnings",
  },
  {
    id: "briefing",
    title: "Start the day aligned",
    blurb: "A clear daily brief turns scattered data into one business view.",
    accent: "#22c55e",
    accentSoft: "rgba(34,197,94,0.24)",
    accentSecondary: "#bef264",
    icon: TrendingUp,
    visual: "briefing",
  },
  {
    id: "answers",
    title: "Move from question to answer",
    blurb: "Ask directly and get grounded context without building more dashboards.",
    accent: "#38bdf8",
    accentSoft: "rgba(56,189,248,0.24)",
    accentSecondary: "#0ea5e9",
    icon: MessageSquareText,
    visual: "answers",
  },
  {
    id: "coverage",
    title: "Keep the whole system in view",
    blurb: "Store performance, traffic, and pressure points stay connected.",
    accent: "#f97316",
    accentSoft: "rgba(249,115,22,0.24)",
    accentSecondary: "#fb7185",
    icon: BellRing,
    visual: "coverage",
  },
];

function FocusVisual({ card }: { card: FocusCard }) {
  if (card.visual === "clarity") {
    return (
      <div className="relative h-full overflow-hidden bg-[linear-gradient(180deg,rgba(2,6,23,0.98),rgba(15,23,42,0.92))]">
        <div className="absolute inset-x-0 bottom-[-10%] top-[30%] bg-[radial-gradient(circle_at_24%_40%,rgba(168,85,247,0.34),transparent_24%),radial-gradient(circle_at_76%_34%,rgba(56,189,248,0.26),transparent_22%)]" />
        <div className="absolute left-[12%] top-[42%] h-48 w-48 rounded-full border border-white/10 blur-[1px]" style={{ backgroundColor: card.accentSoft }} />
        <div className="absolute right-[14%] top-[50%] h-36 w-36 rounded-full border border-white/10 blur-[1px]" style={{ backgroundColor: "rgba(56,189,248,0.16)" }} />
        <div className="absolute left-[22%] top-[46%] grid w-[54%] grid-cols-4 gap-3">
          {[68, 44, 82, 58, 74, 40, 64, 88].map((value, index) => (
            <div key={`${card.id}-${value}-${index}`} className="flex h-36 items-end rounded-[1.6rem] border border-white/10 bg-slate-950/52 p-2 backdrop-blur-sm">
              <div className="w-full rounded-[1rem]" style={{ height: `${value}%`, background: index % 2 === 0 ? `linear-gradient(180deg, ${card.accent}, ${card.accentSecondary})` : `linear-gradient(180deg, ${card.accentSecondary}, ${card.accent})` }} />
            </div>
          ))}
        </div>
        <div className="absolute right-[12%] top-[40%] h-28 w-28 rounded-[2rem] border border-white/10 bg-slate-950/46 backdrop-blur-sm" />
      </div>
    );
  }

  if (card.visual === "warnings") {
    return (
      <div className="relative h-full overflow-hidden bg-[linear-gradient(180deg,rgba(2,6,23,0.98),rgba(15,23,42,0.92))]">
        <div className="absolute inset-x-0 bottom-[-4%] top-[32%] bg-[radial-gradient(circle_at_18%_30%,rgba(239,68,68,0.32),transparent_22%),radial-gradient(circle_at_80%_70%,rgba(249,115,22,0.24),transparent_26%)]" />
        <div className="absolute left-[14%] top-[48%] h-56 w-[46%] rounded-[2.2rem] border border-white/10 bg-slate-950/50 p-4 backdrop-blur-sm">
          <div className="grid h-full grid-rows-4 gap-3">
            {[78, 66, 44, 28].map((value, index) => (
              <div key={`${card.id}-${value}-${index}`} className="overflow-hidden rounded-full bg-slate-900/86">
                <div className="h-full rounded-full" style={{ width: `${value}%`, background: index < 2 ? `linear-gradient(90deg, ${card.accent}, ${card.accentSecondary})` : `linear-gradient(90deg, ${card.accentSecondary}, ${card.accent})` }} />
              </div>
            ))}
          </div>
        </div>
        <div className="absolute right-[14%] top-[42%] flex h-40 w-32 flex-col justify-between rounded-[2rem] border border-white/10 bg-slate-950/52 p-4 backdrop-blur-sm">
          {[24, 42, 66].map((value, index) => (
            <div key={`${card.id}-stack-${index}`} className="rounded-full" style={{ height: `${value}px`, backgroundColor: index === 1 ? card.accentSecondary : card.accentSoft }} />
          ))}
        </div>
      </div>
    );
  }

  if (card.visual === "briefing") {
    return (
      <div className="relative h-full overflow-hidden bg-[linear-gradient(180deg,rgba(2,6,23,0.98),rgba(15,23,42,0.92))]">
        <div className="absolute inset-x-0 bottom-[-10%] top-[28%] bg-[radial-gradient(circle_at_18%_34%,rgba(34,197,94,0.28),transparent_22%),radial-gradient(circle_at_80%_24%,rgba(190,242,100,0.18),transparent_20%)]" />
        <div className="absolute left-[14%] top-[42%] h-[38%] w-[38%] rounded-[2.2rem] border border-white/10 bg-slate-950/50 p-4 backdrop-blur-sm">
          <div className="grid h-full gap-3">
            {[0.74, 0.58, 0.82, 0.46].map((value, index) => (
              <div key={`${card.id}-${index}`} className="overflow-hidden rounded-full bg-slate-900/84 px-2 py-2">
                <div className="h-full rounded-full" style={{ width: `${value * 100}%`, background: `linear-gradient(90deg, ${card.accent}, ${card.accentSecondary})` }} />
              </div>
            ))}
          </div>
        </div>
        <div className="absolute right-[12%] top-[46%] grid h-44 w-44 grid-cols-2 gap-3 rounded-[2rem] border border-white/10 bg-slate-950/50 p-4 backdrop-blur-sm">
          {Array.from({ length: 4 }).map((_, index) => (
            <div key={`${card.id}-tile-${index}`} className="rounded-[1.2rem] border border-white/10" style={{ background: index % 2 === 0 ? `linear-gradient(180deg, ${card.accent}, transparent)` : `linear-gradient(180deg, ${card.accentSecondary}, transparent)` }} />
          ))}
        </div>
      </div>
    );
  }

  if (card.visual === "answers") {
    return (
      <div className="relative h-full overflow-hidden bg-[linear-gradient(180deg,rgba(2,6,23,0.98),rgba(15,23,42,0.92))]">
        <div className="absolute inset-x-0 bottom-[-8%] top-[30%] bg-[radial-gradient(circle_at_76%_20%,rgba(56,189,248,0.32),transparent_18%),radial-gradient(circle_at_18%_80%,rgba(14,165,233,0.18),transparent_22%)]" />
        <div className="absolute right-[14%] top-[42%] flex max-w-[44%] flex-col gap-3">
          <div className="rounded-[1.6rem] rounded-br-md px-5 py-4 shadow-[0_18px_60px_rgba(14,116,144,0.34)]" style={{ background: `linear-gradient(135deg, ${card.accent}, ${card.accentSecondary})` }} />
          <div className="ml-8 rounded-[1.6rem] rounded-bl-md border border-white/10 bg-slate-950/56 px-5 py-6 backdrop-blur-sm" />
        </div>
        <div className="absolute left-[12%] top-[54%] grid w-[42%] grid-cols-3 gap-3">
          {Array.from({ length: 6 }).map((_, index) => (
            <div key={`${card.id}-dot-${index}`} className="h-14 rounded-[1rem] border border-white/10 bg-slate-950/42 backdrop-blur-sm" style={{ opacity: 1 - index * 0.09 }} />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="relative h-full overflow-hidden bg-[linear-gradient(180deg,rgba(2,6,23,0.98),rgba(15,23,42,0.92))]">
      <div className="absolute inset-x-0 bottom-[-10%] top-[28%] bg-[radial-gradient(circle_at_20%_20%,rgba(249,115,22,0.32),transparent_18%),radial-gradient(circle_at_78%_30%,rgba(251,113,133,0.2),transparent_20%)]" />
      <div className="absolute left-[12%] top-[42%] grid w-[50%] gap-3">
        {Array.from({ length: 4 }).map((_, index) => (
          <div key={`${card.id}-lane-${index}`} className="grid grid-cols-[1.3fr_0.7fr] gap-3">
            <div className="h-12 rounded-full border border-white/10 bg-slate-950/50 backdrop-blur-sm" style={{ opacity: 0.9 - index * 0.12 }} />
            <div className="h-12 rounded-full" style={{ background: index % 2 === 0 ? `linear-gradient(90deg, ${card.accent}, ${card.accentSecondary})` : `linear-gradient(90deg, ${card.accentSecondary}, ${card.accent})`, opacity: 0.92 - index * 0.12 }} />
          </div>
        ))}
      </div>
      <div className="absolute right-[12%] top-[48%] flex h-40 w-40 items-center justify-center rounded-full border border-white/10 bg-slate-950/44 backdrop-blur-sm">
        <div className="grid h-24 w-24 grid-cols-3 gap-2">
          {Array.from({ length: 9 }).map((_, index) => (
            <div key={`${card.id}-grid-${index}`} className="rounded-full" style={{ backgroundColor: index % 3 === 0 ? card.accent : index % 2 === 0 ? card.accentSecondary : "rgba(255,255,255,0.16)" }} />
          ))}
        </div>
      </div>
    </div>
  );
}

export function FocusGallery() {
  const railRef = useRef<HTMLDivElement>(null);
  const dragStateRef = useRef({ isDown: false, startX: 0, scrollLeft: 0 });
  const loopCards = useMemo(() => [...focusCards, ...focusCards, ...focusCards], []);

  useEffect(() => {
    const rail = railRef.current;
    if (!rail) {
      return;
    }

    const centerTrack = () => {
      rail.scrollLeft = rail.scrollWidth / 3;
    };

    centerTrack();

    const syncLoopPosition = () => {
      const segmentWidth = rail.scrollWidth / 3;

      if (segmentWidth <= 0) {
        return;
      }

      if (rail.scrollLeft < segmentWidth * 0.5) {
        rail.scrollLeft += segmentWidth;
      } else if (rail.scrollLeft > segmentWidth * 1.5) {
        rail.scrollLeft -= segmentWidth;
      }
    };

    rail.addEventListener("scroll", syncLoopPosition, { passive: true });
    window.addEventListener("resize", centerTrack);

    return () => {
      rail.removeEventListener("scroll", syncLoopPosition);
      window.removeEventListener("resize", centerTrack);
    };
  }, [loopCards]);

  const scrollCards = (direction: "left" | "right") => {
    if (!railRef.current) {
      return;
    }

    const amount = Math.min(railRef.current.clientWidth * 0.72, 720);
    railRef.current.scrollBy({
      left: direction === "left" ? -amount : amount,
      behavior: "smooth",
    });
  };

  const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!railRef.current) {
      return;
    }

    dragStateRef.current = {
      isDown: true,
      startX: event.clientX,
      scrollLeft: railRef.current.scrollLeft,
    };

    railRef.current.setPointerCapture(event.pointerId);
  };

  const handlePointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!railRef.current || !dragStateRef.current.isDown) {
      return;
    }

    const delta = event.clientX - dragStateRef.current.startX;
    railRef.current.scrollLeft = dragStateRef.current.scrollLeft - delta;
  };

  const handlePointerUp = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!railRef.current) {
      return;
    }

    dragStateRef.current.isDown = false;
    railRef.current.releasePointerCapture(event.pointerId);
  };

  return (
    <section className="py-24 bg-slate-900/40 overflow-hidden">
      <div className="px-4 md:px-6">
        <ScrollReveal>
          <div className="mb-10 flex flex-col items-center gap-6 text-center">
            <div className="max-w-4xl">
              <h2 className={`${websiteSectionTitleText} text-white`}>
                Focus on <span className="bg-gradient-to-r from-purple-300 via-white to-cyan-300 bg-[length:200%_auto] bg-clip-text text-transparent drop-shadow-[0_0_24px_rgba(168,85,247,0.28)] animate-[focus-gallery-glow_7s_linear_infinite]">what truly matters</span> when it matters
              </h2>
            </div>
          </div>
        </ScrollReveal>

        <div
          ref={railRef}
          className="flex cursor-grab gap-5 overflow-x-auto pb-4 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden active:cursor-grabbing"
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerUp}
          onPointerLeave={(event) => {
            if (dragStateRef.current.isDown) {
              handlePointerUp(event);
            }
          }}
        >
          {loopCards.map((card, index) => {
            const Icon = card.icon;

            return (
              <div key={`${card.id}-${index}`} className="shrink-0">
                <article className="group relative h-[30rem] w-[62vw] min-w-[18rem] shrink-0 select-none overflow-hidden rounded-[2rem] border border-slate-800/90 bg-slate-950/90 shadow-[0_24px_80px_rgba(2,6,23,0.45)] transition-transform duration-300 ease-out hover:scale-[1.03] md:h-[34rem] md:w-[28vw] md:min-w-[22rem] xl:w-[22vw] xl:min-w-[20rem] 2xl:w-[20vw] 2xl:min-w-[19rem]">
                  <FocusVisual card={card} />
                  <div className="absolute inset-x-0 top-0 h-56 bg-gradient-to-b from-slate-950 via-slate-950/96 via-slate-950/88 to-transparent" />
                  <div className="absolute left-5 top-5 z-10 max-w-[88%] md:left-7 md:top-7">
                    <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-slate-950/72 px-3 py-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-200 backdrop-blur">
                      <Icon className="h-3.5 w-3.5" style={{ color: card.accent }} />
                      <span>Storita system</span>
                    </div>
                    <h3 className={`mt-4 max-w-[10ch] text-white ${websiteCardTitleText}`}>{card.title}</h3>
                    <p className={`mt-3 max-w-[18ch] text-slate-300 ${websiteBodyText}`}>{card.blurb}</p>
                  </div>
                </article>
              </div>
            );
          })}
        </div>

        <div className="mt-6 flex items-center justify-center gap-3">
          <button
            type="button"
            onClick={() => scrollCards("left")}
            className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-slate-700 bg-slate-950/80 text-slate-200 transition-colors hover:border-slate-500 hover:text-white"
            aria-label="Scroll gallery left"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => scrollCards("right")}
            className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-slate-700 bg-slate-950/80 text-slate-200 transition-colors hover:border-slate-500 hover:text-white"
            aria-label="Scroll gallery right"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>

      <style jsx>{`
        @keyframes focus-gallery-glow {
          0% {
            background-position: 0% 50%;
          }

          100% {
            background-position: 200% 50%;
          }
        }
      `}</style>
    </section>
  );
}